import axios, { AxiosError, AxiosRequestConfig } from "axios";
import * as Sentry from "@sentry/react";
import { persistor, store } from "src/store";
import { resetAuthState } from "../auth/authSlice";
import { refreshSession } from "../auth/services/sessionRefresh";
import type { ApiErrorResponse } from "./axios.types";
import { buildTenantSubdomainUrl } from "../companies/utils/tenantUrls";

type ApiAxiosError = AxiosError<ApiErrorResponse>;

const CSRF_HEADER_NAME = "X-CSRFToken";
const CSRF_COOKIE_NAME = "csrftoken";
const REQUESTED_WITH = "XMLHttpRequest";
const CONTENT_TYPE = "application/json";
const ACCEPT = "application/json";
const MAINTENANCE_REDIRECT_URL = "/maintenance";
const TOKEN_REFRESH_ENDPOINT = "/api/token-refresh/";

// One-shot guard. Without it, N parallel in-flight requests hitting 502/503
// each fire `window.location.href = "/maintenance"`, and the browser visibly
// flickers as each navigation pre-empts the previous one.
let redirectingToMaintenance = false;
const goToMaintenance = () => {
  if (redirectingToMaintenance) return;
  if (window.location.pathname === MAINTENANCE_REDIRECT_URL) return;
  redirectingToMaintenance = true;
  window.location.href = MAINTENANCE_REDIRECT_URL;
};

// Error filtering and categorization
const shouldCaptureError = (error: ApiAxiosError): boolean => {
  const status = error.response?.status;

  // Skip cancelled requests (browser navigation, component unmount, etc.)
  if (axios.isCancel(error)) return false;

  // Skip network errors without response (usually cancelled requests or connectivity issues)
  // These often occur when navigation happens during an in-flight request
  if (!error.response && error.message === "Network Error") return false;

  // Skip expected/expected errors that don't need monitoring
  if (status && [401, 403, 404].includes(status)) return false;

  // Skip validation errors (400s with specific patterns)
  if (status === 400 && error.response?.data?.non_field_errors) return false;

  // Capture all other errors
  return true;
};

const getErrorContext = (error: ApiAxiosError) => {
  const status = error.response?.status;
  const url = error.config?.url;
  const method = error.config?.method;

  return {
    tags: {
      component: "axios-client",
      errorType: getErrorType(status),
      severity: getErrorSeverity(status),
      status: status?.toString() || "unknown",
    },
    extra: {
      url,
      method,
      status,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      pathname: window.location.pathname,
    },
  };
};

const getErrorType = (status?: number): string => {
  if (!status) return "network";
  if (status >= 500) return "server";
  if (status >= 400) return "client";
  return "unknown";
};

const getErrorSeverity = (status?: number): string => {
  if (!status || status >= 500) return "error";
  if (status >= 400) return "warning";
  return "info";
};

const axiosClient = axios.create({
  baseURL: window.location.origin,
});

axiosClient.defaults.xsrfHeaderName = CSRF_HEADER_NAME;
axiosClient.defaults.xsrfCookieName = CSRF_COOKIE_NAME;
axiosClient.defaults.withCredentials = true;
axiosClient.defaults.headers.common["X-Requested-With"] = REQUESTED_WITH;
axiosClient.defaults.headers.common["Content-Type"] = CONTENT_TYPE;
axiosClient.defaults.headers.common["Accept"] = ACCEPT;

// Set Accept-Language header from Redux locale state on every request
axiosClient.interceptors.request.use((config) => {
  if (config.data instanceof FormData && config.headers) {
    const headers = config.headers as typeof config.headers & {
      delete?: (header: string) => void;
    };

    if (typeof headers.delete === "function") {
      headers.delete("Content-Type");
    } else {
      delete (headers as Record<string, unknown>)["Content-Type"];
    }
  }

  const locale = store.getState()?.locale?.currentLocale;
  if (locale && config.headers) {
    config.headers["Accept-Language"] = locale;
  }
  return config;
});

// Enhanced Error Handling Function
const handleErrors = async (error: ApiAxiosError) => {
  const originalRequest = error.config as AxiosRequestConfig & {
    _retry?: boolean;
  };

  // Capture error to Sentry with context (if it should be captured)
  if (shouldCaptureError(error)) {
    const context = getErrorContext(error);
    Sentry.captureException(error, context);
  }

  if (error?.response && error?.response?.status) {
    // Check if response data contains HTML and replace with clean message.
    // Cast through `unknown` because ApiErrorResponse types data as an object;
    // some upstream errors return raw HTML strings (e.g. nginx 502 page).
    const rawData = error.response.data as unknown;
    if (
      rawData &&
      typeof rawData === "string" &&
      rawData.includes("<") &&
      rawData.includes(">")
    ) {
      error.response.data = {
        detail: "A server error occurred. Please try again.",
      };
    }

    switch (error?.response?.status) {
      case 502: // Bad Gateway error (site maintenance)
        goToMaintenance();
        break;

      case 503: {
        // Service Unavailable (scheduled maintenance)
        const retryAfterIso = error.response?.data?.retry_after_iso;
        if (retryAfterIso) {
          sessionStorage.setItem("maintenance_retry_after_iso", retryAfterIso);
        }
        goToMaintenance();
        break;
      }

      case 504: // Gateway Timeout error
        error.response.data = {
          detail:
            "The server is taking too long to respond. Please try again in a few minutes.",
        };
        break;

      case 401: {
        // Unauthorized error (user not logged in)
        // Skip token refresh if this is the refresh endpoint itself or if we've already retried
        if (
          originalRequest?.url?.endsWith(TOKEN_REFRESH_ENDPOINT) ||
          originalRequest?._retry
        ) {
          const authState = store.getState().auth;
          if (authState.isAuthenticated) {
            store.dispatch(resetAuthState());
            persistor.purge();
            window.location.href = "/";
          }
          return Promise.reject(error);
        }

        // Refresh once and retry. The refresh token is an HttpOnly cookie the
        // browser sends itself, so there is nothing to check for here: only a
        // failed refresh ends the session. Parallel 401s share the one refresh
        // (rotation blacklists the old token, so two would cancel each other).
        // The new access token goes into state too: the websocket URL is
        // built from it.
        // Signed out (a wrong password at login, say): nothing to refresh,
        // and the caller gets the 401 it caused.
        if (!store.getState().auth.isAuthenticated) {
          return Promise.reject(error);
        }
        originalRequest._retry = true;
        try {
          await refreshSession();
          return axiosClient(originalRequest);
        } catch (refreshError) {
          const authState = store.getState().auth;
          if (authState.isAuthenticated) {
            store.dispatch(resetAuthState());
            persistor.purge();
            window.location.href = "/";
          }
          return Promise.reject(refreshError);
        }
      }

      case 403: // Forbidden error (wrong subdomain)
        if (error.response.data.subdomain) {
          const subdomain = error.response.data.subdomain;
          const url = buildTenantSubdomainUrl(subdomain);

          // Redirect the user to the correct subdomain
          if (url !== window.location.href) {
            window.location.href = url;
          }

          // Return Promise.reject to skip the next catch block
          return Promise.reject(error);
        }
        break;
      // TODO: Add additional cases for different HTTP statuses
      default:
        console.error(`An error occurred: ${error}`);
    }
  } else {
    console.error(`An error occurred: ${error}`);
  }
  return Promise.reject(error);
};

axiosClient.interceptors.response.use((response) => response, handleErrors);

export default axiosClient;
